import { z } from "zod";

export const rutenPublishImageSchema = z.object({
  url: z.string().trim().url(),
  order: z.number().int().min(0).max(8),
  source: z.enum(["remote", "product"]).default("product")
});

export const rutenPublishVariantAttributeSchema = z.object({
  name: z.string().trim().min(1).max(20),
  value: z.string().trim().min(1).max(20)
});

export const rutenPublishVariantSchema = z.object({
  id: z.string().trim().min(1).max(80).optional(),
  name: z.string().trim().min(1).max(80),
  sku: z.string().trim().max(80).optional().nullable(),
  priceAmount: z.string().trim().min(1),
  inventoryQuantity: z.number().int().min(0).max(999999),
  available: z.boolean().default(true),
  attributes: z.array(rutenPublishVariantAttributeSchema).min(1).max(2)
});

export const rutenPublishShippingProfileSchema = z.object({
  shipMode: z.string().trim().min(1),
  paymentMethods: z.array(z.string().trim().min(1)).min(1).max(20),
  shippingFees: z.record(z.string().trim().min(1), z.number().int().min(0).max(9999)),
  acceptShipRule: z.boolean().default(true)
});

export const rutenPublishOptionsSchema = z.object({
  stockStatus: z.string().trim().min(1),
  buyerLimitNum: z.number().int().min(0).max(999999),
  defaultLocation: z.string().trim().min(1),
  shippingProfile: rutenPublishShippingProfileSchema
});

export const rutenPublishItemSchema = z.object({
  productId: z.string().trim().min(1),
  sourceUrl: z.string().trim().url(),
  externalProductId: z.string().trim().optional().nullable(),
  title: z.string().trim().min(1).max(60),
  sourceTitle: z.string().trim().min(1).optional(),
  titleTruncated: z.boolean().default(false),
  descriptionHtml: z.string().trim().min(1),
  priceAmount: z.string().trim().min(1),
  currency: z.string().trim().min(1).default("TWD"),
  categoryId: z.string().trim().regex(/^\d{1,32}$/),
  categoryPath: z.array(z.string().trim().min(1)).max(12).default([]),
  conditionText: z.string().trim().min(1).default("全新"),
  conditionCode: z.string().trim().min(1).default("B"),
  locationText: z.string().trim().min(1),
  inventoryQuantity: z.number().int().min(0).max(999999),
  goodsNo: z.string().trim().max(80).optional().nullable(),
  images: z.array(rutenPublishImageSchema).min(1).max(9),
  variants: z.array(rutenPublishVariantSchema).max(300).default([]),
  tags: z.array(z.string().trim().min(1).max(20)).max(10).default([]),
  videoLink: z.string().trim().url().optional().nullable()
});

export const rutenPublishBatchSchema = z.object({
  batchId: z.string().trim().regex(/^batch_/),
  platform: z.literal("ruten"),
  mode: z.literal("ruten_web_form_http"),
  tenantId: z.string().trim().min(1),
  storeId: z.string().trim().min(1),
  items: z.array(rutenPublishItemSchema).min(1).max(100),
  options: rutenPublishOptionsSchema,
  createdAt: z.string().datetime(),
  ruleBundleVersion: z.string().trim().optional().nullable(),
  ruleWarningCount: z.number().int().min(0),
  ruleErrorCount: z.number().int().min(0)
});

export const rutenPublishItemResultSchema = z.object({
  productId: z.string().trim().min(1),
  status: z.enum(["published", "failed"]),
  title: z.string().trim().min(1),
  categoryId: z.string().trim().min(1),
  platformListingId: z.string().trim().optional().nullable(),
  platformListingUrl: z.string().trim().url().optional().nullable(),
  previewUrl: z.string().trim().url().optional().nullable(),
  imageUploadCount: z.number().int().min(0),
  errorMessage: z.string().trim().optional().nullable()
});

export const rutenPublishBatchResultSchema = z.object({
  mode: z.literal("ruten_web_form_http"),
  targetPlatform: z.literal("ruten"),
  platformRequestSent: z.literal(true),
  dryRun: z.literal(false),
  action: z.literal("listing.publish_batch"),
  resourceType: z.literal("ruten_publish_batch"),
  resourceId: z.string().trim().min(1).optional().nullable(),
  confirmationId: z.string().trim().min(1),
  summary: z.string().trim().min(1),
  errorMessage: z.string().trim().optional().nullable(),
  batch: z.object({
    batchId: z.string().trim().min(1),
    total: z.number().int().min(0),
    succeeded: z.number().int().min(0),
    failed: z.number().int().min(0),
    items: z.array(rutenPublishItemResultSchema)
  })
});

export type RutenPublishImage = z.infer<typeof rutenPublishImageSchema>;
export type RutenPublishVariantAttribute = z.infer<typeof rutenPublishVariantAttributeSchema>;
export type RutenPublishVariant = z.infer<typeof rutenPublishVariantSchema>;
export type RutenPublishShippingProfile = z.infer<typeof rutenPublishShippingProfileSchema>;
export type RutenPublishOptions = z.infer<typeof rutenPublishOptionsSchema>;
export type RutenPublishItem = z.infer<typeof rutenPublishItemSchema>;
export type RutenPublishBatch = z.infer<typeof rutenPublishBatchSchema>;
export type RutenPublishItemResult = z.infer<typeof rutenPublishItemResultSchema>;
export type RutenPublishBatchResult = z.infer<typeof rutenPublishBatchResultSchema>;
